import { IconButton, IconButtonType, Spinner } from "@whitespace/whitespace-ui";
import {
  Card, Col, Row, Badge
} from "react-bootstrap";
import {
  ColumnDirective,
  ColumnsDirective,
  Filter, FilterSettingsModel, GridComponent, Inject, PageSettingsModel, Resize, Sort, Page as Paging
} from "@syncfusion/ej2-react-grids";
import { parseUsingParseInt } from "helpers/stringUtils";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { useEffect, useState } from "react";
import SiteDiaryItemOrdersProps from "interfaces/SiteDiary/ItemOrder/SiteDiaryItemOrdersProps";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { useNavigate } from "react-router-dom";
import { useStateMachine } from "little-state-machine";
import { updateCreateItemOrderPageOrigination } from "actions/updateCreateItemOrderPageDetails";
import InductedTemplate from "./Templates/StatusTemplate";
import EditTemplate from "./Templates/EditTemplate";
import ItemTemplate from "./Templates/ItemTemplate";
import DetailsTemplate from "./Templates/DetailsTemplate";

const SiteDiaryItemOrders = ({
  diaryDate, hwrcSiteId, diarySaved, itemOrders
} : SiteDiaryItemOrdersProps) => {
  const navigate = useNavigate();

  const { actions } = useStateMachine({
    updateCreateItemOrderPageOrigination
  });

  const [renderingComplete, setRenderingComplete] = useState(false);
  const [isGridRendered, setIsGridRendered] = useState(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(true);

  const pageSize: number = parseUsingParseInt(process.env.REACT_APP_DIARY_CARD_PAGE_SIZE);
  const pageSettings: PageSettingsModel = {
    pageSize,
    pageSizes: [5, 10, 20, 50, "All"]
  };

  const filterSettings: FilterSettingsModel = { type: "Excel" };
  let grid: GridComponent | undefined;

  const itemTemplateInstance = ItemTemplate;
  const detailsTemplateInstance = DetailsTemplate;
  const statusTemplateInstance = InductedTemplate;
  const editTemplateInstance = EditTemplate;

  const gridCreated = () => {
    setIsGridRendered(true);
  };

  const handleAddButtonClick = () => {
    actions.updateCreateItemOrderPageOrigination(RoutePathsEnum.SiteDiaryLandingPage);
    navigate(`${RoutePathsEnum.ItemOrdersCreate}/${hwrcSiteId}/${diaryDate}`);
  };

  useEffect(() => {
    if (isGridRendered && renderingComplete) {
      setShowSpinner(false);
    }
  }, [isGridRendered, renderingComplete, setShowSpinner]);

  return (
    <Card>
      <Card.Header>
        <Row>
          <Col xs={8} className="header" data-testid="header">
            <h3>
              Item Orders
              <Spinner
                animation="grow"
                visible={showSpinner}
                spinnerText="Loading"
                variant="info"
              />
            </h3>
          </Col>
          <Col xs={4} className="right-pane">
            <div className="d-flex">
              <IconButton
                data-testid="addButtonItemOrder"
                className="me-2"
                iconType={IconButtonType.Add}
                disabled={!diarySaved}
                onClick={handleAddButtonClick}
              >
                Add
              </IconButton>
              <Badge data-testid="rowCounter">{diarySaved ? itemOrders.length : 0}</Badge>
            </div>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="no-padding">
        <Row className="mb-2 ms-2 mt-2 me-2 w-auto">
          <Col>
            <GridComponent
              ref={(g: GridComponent) => {
                grid = g;
              }}
              id="gridItemOrders"
              data-testid="gridItemOrders"
              allowPaging
              allowResizing
              pageSettings={pageSettings}
              filterSettings={filterSettings}
              allowSelection={false}
              dataSource={diarySaved && itemOrders}
              actionComplete={() => {
                makeGridAACompliant(grid!, () => {
                  setRenderingComplete(true);
                });
              }}
              loadingIndicator={{ indicatorType: "Shimmer" }}
              created={gridCreated}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="id"
                  headerText="id"
                  visible={false}
                  isPrimaryKey
                />
                <ColumnDirective
                  field="orderType"
                  headerText="Item"
                  width={250}
                  template={itemTemplateInstance}
                />
                <ColumnDirective
                  field="quantity"
                  headerText="Details"
                  template={detailsTemplateInstance}
                />
                <ColumnDirective
                  field="recipient"
                  headerText="Recipient"
                />
                <ColumnDirective
                  field="status"
                  headerText="Status"
                  template={statusTemplateInstance}
                  width={100}
                />
                <ColumnDirective
                  allowSorting={false}
                  allowFiltering={false}
                  template={editTemplateInstance}
                  width={36}
                />
              </ColumnsDirective>
              <Inject services={[Filter, Paging, Sort, Resize]} />
            </GridComponent>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default SiteDiaryItemOrders;
